import { useEffect, useState, useRef } from 'react';
import { Bell, UserPlus, Info, X, CheckCircle2 } from 'lucide-react';
import { useNotificationStore } from '../../store/notificationStore';

export const NotificationDropdown = () => {
  const { notifications, fetchNotifications, acceptInvite, markAsRead } = useNotificationStore();
  const [isOpen, setIsOpen] = useState(false);
  const [isAccepting, setIsAccepting] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleNotificationClick = async (n: typeof notifications[0]) => {
    if (!n.read) {
      await markAsRead(n._id);
    }
  };

  const handleAcceptInvite = async (e: React.MouseEvent, notification: typeof notifications[0]) => {
    e.stopPropagation();
    setIsAccepting(notification._id);
    try {
      await acceptInvite(notification);
    } catch (error) {
      console.error('Failed to accept invite:', error);
    } finally {
      setIsAccepting(null);
    }
  };

  const getNotificationIcon = (type: string, read: boolean) => {
    const iconClass = `w-5 h-5 mt-0.5 ${read ? 'text-gray-400' : 'text-primary-500'}`;

    switch (type) {
      case 'INVITE':
        return <UserPlus className={iconClass} />;
      default:
        return <Info className={iconClass} />;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;

    return date.toLocaleDateString();
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Bell Icon with Badge */}
      <button
        className="p-3 bg-gray-50 dark:bg-gray-700 rounded-full text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:shadow-md transition-all duration-200 shadow-sm"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <div className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse shadow-lg">
            {unreadCount > 9 ? '9+' : unreadCount}
          </div>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-3 w-96 bg-white border border-gray-200 rounded-2xl shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                <p className="text-gray-500 text-sm">
                  {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
                aria-label="Close notifications"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Bell className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 text-sm font-medium">No notifications yet</p>
                <p className="text-gray-400 text-xs mt-1">We'll let you know when something happens</p>
              </div>
            ) : (
              notifications.map((notification, index) => (
                <div
                  key={notification._id}
                  className={`relative p-4 border-b border-gray-100 cursor-pointer transition-all duration-200 hover:bg-gray-50 ${!notification.read ? 'bg-primary-50/50' : ''
                    } ${index === notifications.length - 1 ? 'border-b-0' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  {/* Unread indicator */}
                  {!notification.read && (
                    <div className="absolute left-2 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-primary-500 rounded-full"></div>
                  )}

                  <div className="flex items-start gap-3 ml-3">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${notification.type === 'INVITE' ? 'bg-primary-100' : 'bg-gray-100'
                      }`}>
                      {getNotificationIcon(notification.type, notification.read)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-relaxed ${!notification.read ? 'text-gray-900 font-medium' : 'text-gray-700'
                        }`}>
                        {notification.message}
                      </p>

                      <div className="flex items-center gap-2 mt-1">
                        <time className="text-xs text-gray-500">
                          {formatTimeAgo(notification.createdAt)}
                        </time>
                        {!notification.read && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                            <div className="w-1 h-1 bg-primary-500 rounded-full"></div>
                            New
                          </span>
                        )}
                      </div>

                      {/* Accept Button */}
                      {notification.type === 'INVITE' && (
                        <div className="mt-3 flex gap-2">
                          <button
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                            onClick={(e) => handleAcceptInvite(e, notification)}
                            disabled={isAccepting === notification._id}
                          >
                            {isAccepting === notification._id ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Accepting...
                              </>
                            ) : (
                              <>
                                <CheckCircle2 className="w-4 h-4" />
                                Accept Invitation
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  {notifications.length} total notification{notifications.length !== 1 ? 's' : ''}
                </p>
                {unreadCount > 0 && (
                  <button
                    onClick={() => {
                      notifications.filter(n => !n.read).forEach(n => markAsRead(n._id));
                    }}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
                  >
                    Mark all as read
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};